import { AxiosResponse } from "axios";
import {
    ApproveLoteRequest,
    Armador,
    BundleAssignmentDashboard,
    Bota,
    CreateLoteRequest,
    DevolucionesMap,
    Lote,
    PasoDevolucion,
    ResultadoRevision,
    RevisionesMap,
    SubmitRevisionRequest,
} from "../../interfaces";
import { resolveReturnBundleState } from "../utils/bundle-assignment.utils";

const ARMADORES: Armador[] = [
    { id: "armador-1", name: "José Ramírez" },
    { id: "armador-2", name: "Luis Gómez" },
    { id: "armador-3", name: "Carlos Slim" },
    { id: "armador-4", name: "Pedro Páez" },
    { id: "armador-5", name: "Juan Pérez" },
    { id: "armador-6", name: "Miguel Rojas" },
];

const mockDate = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();

const mockStepEntryDate = (daysAgo: number) => {
    const date = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
    return date.toISOString().slice(0, 10);
};

const mockDiasRestantesPaso = (daysAgo: number) => {
    const overdueDays = daysAgo + 5;
    const suffix = overdueDays === 1 ? "día" : "días";
    return `-${overdueDays} ${suffix}`;
};

const mockBota = (
    orderCode: number,
    daysAgo: number,
    estado: Bota["estado"],
    precio?: number
): Bota => ({
    id: String(orderCode),
    orderCode,
    fechaIngresoPaso: mockStepEntryDate(daysAgo),
    diasRestantesPaso: mockDiasRestantesPaso(daysAgo),
    estado,
    ...(precio !== undefined ? { precio } : {}),
});

const mockBotaWithPaso = (
    orderCode: number,
    daysAgo: number,
    estado: Bota["estado"],
    perfilProduccionNombre: string,
    perfilProduccionId: number,
    extras: Partial<Bota> = {}
): Bota => ({
    ...mockBota(orderCode, daysAgo, estado),
    perfilProduccionId,
    perfilProduccionNombre,
    pasoProduccionId: perfilProduccionId + 8,
    pasoProduccionNombre: perfilProduccionNombre,
    exportableEnReporte: /^empiernado$/i.test(perfilProduccionNombre.trim()),
    ...extras,
});

const createStartingInventory = (): Bota[] => {
    const boots: Bota[] = Array.from({ length: 12 }).map((_, i) =>
        mockBota(71000 + i, i + 1, "En Asignacion")
    );
    boots.push(
        mockBota(71125, 2, "En Asignacion"),
        mockBota(71126, 1, "En Asignacion")
    );
    return boots;
};

const createStartingBundles = (): Lote[] => [
    {
        id: "LOTE-900",
        armador: "José Ramírez",
        botas: [
            mockBota(70901, 5, "En Armado", 120),
            mockBota(70902, 4, "En Armado", 85),
        ],
        fechaSalida: mockDate(2),
        estado: "En Armado",
    },
    {
        id: "LOTE-901",
        armador: "Luis Gómez",
        botas: [
            mockBota(70801, 8, "En Armado", 150),
            mockBota(70802, 7, "En Armado", 150),
            mockBota(70803, 6, "En Armado", 60),
        ],
        fechaSalida: mockDate(6),
        estado: "En Armado",
    },
    {
        id: "LOTE-902",
        armador: "Carlos Slim",
        botas: [mockBota(70701, 10, "En Armado", 120)],
        fechaSalida: mockDate(8),
        estado: "En Armado",
    },
    {
        id: "LOTE-850",
        armador: "Pedro Páez",
        botas: [
            {
                ...mockBota(70601, 3, "En Armado", 85),
                revisionAvanzada: true,
                fechaVerificacion: mockStepEntryDate(2),
            },
            mockBota(70602, 3, "En Armado", 85),
        ],
        fechaSalida: mockDate(10),
        estado: "Incompleto",
    },
    {
        id: "LOTE-904",
        armador: "Juan Pérez",
        botas: [
            mockBotaWithPaso(70401, 4, "En Armado", "Revisión armado", 18, {
                revisionAvanzada: true,
                fechaVerificacion: mockStepEntryDate(2),
            }),
            mockBotaWithPaso(70402, 4, "En Armado", "Empiernado", 13, {
                revisionAvanzada: true,
                fechaVerificacion: mockStepEntryDate(2),
            }),
            mockBotaWithPaso(70403, 4, "En Armado", "Terminado", 15, {
                revisionAvanzada: true,
                fechaVerificacion: mockStepEntryDate(2),
            }),
        ],
        fechaSalida: mockDate(10),
        fechaCompleto: mockDate(2),
        estado: "Completo",
    },
    {
        id: "LOTE-903",
        armador: "Miguel Rojas",
        botas: [
            mockBota(70501, 6, "En Armado", 120),
            mockBota(70502, 5, "En Armado", 60),
        ],
        fechaSalida: mockDate(3),
        estado: "En Revision",
    },
];

let inventario: Bota[] = createStartingInventory();
let lotes: Lote[] = createStartingBundles();
let revisiones: RevisionesMap = {
    "LOTE-850": { "70601": "OK" },
    "LOTE-904": { "70401": "OK", "70402": "OK", "70403": "OK" },
};
let devoluciones: DevolucionesMap = {};

const toAxiosResponse = <T>(data: T): AxiosResponse<T> => ({
    data,
    status: 200,
    statusText: "OK",
    headers: {},
    config: {},
});

const clearLoteReviewData = (loteId: string) => {
    const nextRevisiones = { ...revisiones };
    const nextDevoluciones = { ...devoluciones };
    delete nextRevisiones[loteId];
    delete nextDevoluciones[loteId];
    revisiones = nextRevisiones;
    devoluciones = nextDevoluciones;
};

const buildDashboard = (): BundleAssignmentDashboard => ({
    inventario: [...inventario],
    lotes: lotes.map((l) => ({ ...l, botas: [...l.botas] })),
    armadores: [...ARMADORES],
    revisiones: JSON.parse(JSON.stringify(revisiones)),
    devoluciones: JSON.parse(JSON.stringify(devoluciones)),
});

export const bundleAssignmentMock = {
    getDashboard(): Promise<AxiosResponse<BundleAssignmentDashboard>> {
        return Promise.resolve(toAxiosResponse(buildDashboard()));
    },

    createLote(
        payload: CreateLoteRequest,
        baseDashboard?: BundleAssignmentDashboard | null
    ): Promise<AxiosResponse<BundleAssignmentDashboard>> {
        const sourceInventory = baseDashboard?.inventario ?? inventario;
        const bootsToAdd = sourceInventory.filter((b) => payload.botaIds.includes(b.id));
        if (bootsToAdd.length === 0 || !payload.armador) {
            return Promise.reject(new Error("Invalid create bundle request"));
        }

        const armadorName =
            baseDashboard?.armadores.find((armador) => armador.id === payload.armador)?.name ||
            ARMADORES.find((armador) => armador.id === payload.armador)?.name ||
            payload.armador;

        const newBundle: Lote = {
            id: `LOTE-${1000 + lotes.length}`,
            armador: armadorName,
            botas: bootsToAdd.map((b) => ({ ...b, estado: "En Armado" })),
            fechaSalida: new Date().toISOString(),
            estado: "En Armado",
        };

        lotes = [newBundle, ...lotes];

        if (baseDashboard) {
            const nextDashboard: BundleAssignmentDashboard = {
                ...baseDashboard,
                inventario: sourceInventory.filter((b) => !payload.botaIds.includes(b.id)),
                lotes: [newBundle, ...(baseDashboard.lotes || [])],
                revisiones: baseDashboard.revisiones || {},
                devoluciones: baseDashboard.devoluciones || {},
            };
            return Promise.resolve(toAxiosResponse(nextDashboard));
        }

        inventario = inventario.filter((b) => !payload.botaIds.includes(b.id));
        return Promise.resolve(toAxiosResponse(buildDashboard()));
    },

    submitRevision(
        loteId: string,
        payload: SubmitRevisionRequest
    ): Promise<AxiosResponse<BundleAssignmentDashboard>> {
        const lote = lotes.find((l) => l.id === loteId);
        if (!lote) {
            return Promise.reject(new Error("Bundle not found"));
        }

        revisiones = {
            ...revisiones,
            [loteId]: {
                ...(revisiones[loteId] || {}),
                [payload.botaId]: payload.resultado,
            },
        };

        const loteDevoluciones = { ...(devoluciones[loteId] || {}) };

        if (payload.resultado === "Devolver" && payload.pasoDevolucion) {
            loteDevoluciones[payload.botaId] = payload.pasoDevolucion;
        } else {
            delete loteDevoluciones[payload.botaId];
        }

        devoluciones = {
            ...devoluciones,
            [loteId]: loteDevoluciones,
        };

        return Promise.resolve(toAxiosResponse(buildDashboard()));
    },

    approveLote(
        loteId: string,
        payload?: ApproveLoteRequest
    ): Promise<AxiosResponse<BundleAssignmentDashboard>> {
        const lote = lotes.find((l) => l.id === loteId);
        if (!lote) {
            return Promise.reject(new Error("Bundle not found"));
        }

        const currentReview = payload?.revisiones || revisiones[loteId] || {};
        const loteDevoluciones = devoluciones[loteId] || {};
        const reviewedCount = Object.keys(currentReview).length;
        const isFullyReviewed = reviewedCount === lote.botas.length;

        if (!isFullyReviewed) {
            return Promise.reject(new Error("All boots must be reviewed before saving"));
        }

        const reviewValues = Object.values(currentReview) as ResultadoRevision[];

        if (lote.estado === "En Revision") {
            const devolverBoots = lote.botas.filter((b) => currentReview[b.id] === "Devolver");

            if (devolverBoots.length > 0) {
                const pasos: PasoDevolucion[] = devolverBoots.map((b) => loteDevoluciones[b.id]).filter(Boolean);

                if (pasos.length !== devolverBoots.length) {
                    return Promise.reject(new Error("All returned boots must have a return step"));
                }

                const { estado, fechaSalidaOffsetDays } = resolveReturnBundleState(pasos);

                lotes = lotes.map((item) =>
                    item.id === loteId
                        ? {
                              ...item,
                              estado,
                              fechaSalida: mockDate(fechaSalidaOffsetDays),
                          }
                        : item
                );
            } else if (reviewValues.every((value) => value === "OK")) {
                lotes = lotes.map((item) =>
                    item.id === loteId ? { ...item, estado: "Cerrado" } : item
                );
            }

            clearLoteReviewData(loteId);
            return Promise.resolve(toAxiosResponse(buildDashboard()));
        }

        if (lote.estado !== "En Armado" && lote.estado !== "Incompleto") {
            return Promise.reject(new Error("Bundle cannot be edited in its current state"));
        }

        if (lote.estado === "Incompleto") {
            const savedReview = revisiones[loteId] || {};
            const invalidRevert = lote.botas.some((boot) => {
                const wasOk = savedReview[boot.id] === "OK" || boot.revisionAvanzada === true;
                return wasOk && currentReview[boot.id] === "Pendiente";
            });
            if (invalidRevert) {
                return Promise.reject(
                    new Error("Orders already verified OK cannot be set to Pendiente")
                );
            }
        }

        const hasPendiente = reviewValues.includes("Pendiente");
        const newStatus: Lote["estado"] = hasPendiente ? "Incompleto" : "Completo";

        lotes = lotes.map((item) =>
            item.id === loteId
                ? {
                      ...item,
                      estado: newStatus,
                      fechaCompleto: newStatus === "Completo" ? new Date().toISOString() : item.fechaCompleto,
                      botas: item.botas.map((boot) => {
                          const isOk = currentReview[boot.id] === "OK";
                          const wasOk = boot.revisionAvanzada || revisiones[loteId]?.[boot.id] === "OK";
                          return {
                              ...boot,
                              revisionAvanzada: isOk ? true : boot.revisionAvanzada,
                              fechaVerificacion:
                                  isOk && !wasOk
                                      ? new Date().toISOString().slice(0, 10)
                                      : boot.fechaVerificacion,
                          };
                      }),
                  }
                : item
        );

        revisiones = {
            ...revisiones,
            [loteId]: { ...currentReview },
        };

        return Promise.resolve(toAxiosResponse(buildDashboard()));
    },

    reset(): void {
        inventario = createStartingInventory();
        lotes = createStartingBundles();
        revisiones = {};
        devoluciones = {};
    },
};
